import { useQuery } from '@tanstack/react-query';
import { trpc } from '../../lib/trpc';

/**
 * How often the rail re-asks while a tab is open.
 *
 * A MINUTE, and the number is a cost decision as much as a freshness one (CLAUDE.md §1.5). The
 * producers are the worker's drains and mail arriving, neither second-by-second, and one small
 * indexed read per tab per minute is what a console left open on a desk all day costs.
 * `refetchOnWindowFocus` is already on globally, so coming back to the tab refreshes it at once
 * and this only covers the tab nobody has touched.
 */
const POLL_MS = 60_000;

/**
 * The numbers on the rail: unread alerts, how many of those are urgent, and unread email.
 *
 * ONE REQUEST FOR ALL OF THEM (`alerts.badges`), because they are shown together on every page
 * and asked for on the same clock. Every badge reads this — the bell, the Communications item,
 * the phone's More tab, the Inbox tab — so two of them cannot disagree, and React Query makes it
 * one request however many are mounted.
 *
 * Undefined until the first answer.
 */
export function useRailBadges() {
  return useQuery(
    trpc.alerts.badges.queryOptions(undefined, {
      staleTime: 30_000,
      refetchInterval: POLL_MS,
      // A failing poll is one quiet notice, not one a minute — see `quietRepeat`.
      meta: { errorTitle: 'Could not check for new notifications' },
    }),
  ).data;
}

/**
 * The colour of a badge counting alerts. Red is for a fault (DESIGN_SYSTEM.md §3): only while
 * something urgent — a rule that stopped, a message nobody received — is unread. Work that is
 * merely waiting is the same blue as unread mail.
 */
export function alertBadgeTone(urgent: number): 'danger' | 'accent' {
  return urgent > 0 ? 'danger' : 'accent';
}
